import { useState } from 'react';
import { View, Text, TextInput, Pressable } from 'react-native';
import { CheckCircle2, Send } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { supabase } from '@/lib/supabase/client';
import { handleError } from '@/lib/errors';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { TurnstileWidget, TURNSTILE_SITE_KEY } from '@/components/submission/TurnstileWidget';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export function ContactMessageForm() {
  const colors = usePublicThemeColors();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const inputStyle = {
    backgroundColor: colors.surface.primary,
    borderWidth: 1,
    borderColor: colors.border.light,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    color: colors.text.primary,
    fontSize: 14,
  } as const;

  const isSubmitDisabled =
    !name.trim() || !email.trim() || message.trim().length < 10 || !captchaToken || formState === 'submitting';

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    setFormState('submitting');
    setErrorMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('contact-message', {
        body: {
          name: name.trim(),
          email: email.trim(),
          topic: topic.trim() || undefined,
          message: message.trim(),
          captchaToken,
        },
      });
      if (error) throw error;
      if (!data?.sent) throw new Error('Message could not be sent');
      setFormState('success');
    } catch (err) {
      const { message: userMessage, isCancelled } = handleError(err, { context: 'ContactMessageForm.handleSubmit' });
      if (!isCancelled) {
        setErrorMessage(userMessage);
        setFormState('error');
      } else {
        setFormState('idle');
      }
    }
  };

  if (formState === 'success') {
    return (
      <Animated.View
        entering={FadeInUp.duration(300)}
        className="p-6 rounded-2xl items-center"
        style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
      >
        <CheckCircle2 size={32} color={colors.primary[600]} />
        <Text style={{ color: colors.text.primary }} className="font-semibold text-base mt-3 mb-1">
          Message sent
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm text-center">
          Thanks for reaching out — we typically respond within one business day.
        </Text>
      </Animated.View>
    );
  }

  return (
    <View className="gap-3">
      <View className="flex-row gap-3">
        <TextInput
          value={name}
          onChangeText={setName}
          placeholder="Your name *"
          placeholderTextColor={colors.text.tertiary}
          autoCapitalize="words"
          editable={formState !== 'submitting'}
          style={{ ...inputStyle, flex: 1 }}
        />
        <TextInput
          value={email}
          onChangeText={setEmail}
          placeholder="Your email *"
          placeholderTextColor={colors.text.tertiary}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          editable={formState !== 'submitting'}
          style={{ ...inputStyle, flex: 1 }}
        />
      </View>
      <TextInput
        value={topic}
        onChangeText={setTopic}
        placeholder="Topic (optional)"
        placeholderTextColor={colors.text.tertiary}
        autoCapitalize="sentences"
        editable={formState !== 'submitting'}
        style={inputStyle}
      />
      <TextInput
        value={message}
        onChangeText={setMessage}
        placeholder="How can we help? *"
        placeholderTextColor={colors.text.tertiary}
        multiline
        numberOfLines={5}
        textAlignVertical="top"
        editable={formState !== 'submitting'}
        style={{ ...inputStyle, minHeight: 120, paddingTop: 12 }}
      />

      <TurnstileWidget
        siteKey={TURNSTILE_SITE_KEY}
        onVerify={(token) => setCaptchaToken(token)}
        onExpire={() => setCaptchaToken(null)}
        onError={() => setCaptchaToken(null)}
        theme="auto"
        size="normal"
      />

      {formState === 'error' && errorMessage && (
        <Animated.View entering={FadeInUp.duration(300)}>
          <Text style={{ color: '#DC2626', fontSize: 14 }} numberOfLines={2}>
            {errorMessage}
          </Text>
        </Animated.View>
      )}

      <View style={{ height: 46, borderRadius: 12, backgroundColor: isSubmitDisabled ? colors.primary[200] : colors.primary[600], overflow: 'hidden' }}>
        <Pressable
          onPress={handleSubmit}
          disabled={isSubmitDisabled}
          style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <Send size={16} color="#FFFFFF" />
          <Text className="text-white font-semibold" numberOfLines={1}>
            {formState === 'submitting' ? 'Sending…' : 'Send Message'}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
